import React, { type ReactNode, useState } from 'react';
import { Text, mergeClasses } from '@fluentui/react-components';
import { useHeroBannerStyles } from './HeroBanner.styles';

export interface HeroBannerProps {
  title: string;
  subtitle: string;
  backgroundImage?: string;
  /**
   * Opt-in to the Figma-aligned 2026 hero style: light navy canvas, eyebrow
   * slot, action row, and an optional floating tip card slot. Defaults to
   * `false` so existing usages render the legacy gradient hero unchanged.
   */
  figmaVariant?: boolean;
  /** Small eyebrow label rendered above the title (figmaVariant only). */
  eyebrow?: string;
  /** Optional CTA / action row rendered to the right of the title block. */
  actions?: ReactNode;
  /** Optional floating tip card rendered to the far right of the hero. */
  tipCard?: ReactNode;
  /**
   * Optional back-link slot rendered above the title (figmaVariant only).
   * Used by the booking wizard hero to render the "Back to bookings" link.
   */
  backLink?: ReactNode;
  /**
   * Optional stepper slot rendered along the bottom of the hero, spanning the
   * full width below the title block + tipCard row (figmaVariant only). Used
   * by the booking wizard hero to render the progress stepper.
   */
  stepper?: ReactNode;
  /**
   * spec 101 FR-001/FR-002: home-screen alignment variant. When `true`, the
   * inner content (title/description/CTA on the left, tipCard on the right)
   * gets extra horizontal padding so it lines up with the "Your Booking
   * Requests" section heading below the banner (5% of viewport). The banner
   * card width itself is unchanged. Use only on the home hero.
   */
  homeAlignedContent?: boolean;
}

export const HeroBanner: React.FC<HeroBannerProps> = ({
  title,
  subtitle,
  backgroundImage,
  figmaVariant = false,
  eyebrow,
  actions,
  tipCard,
  backLink,
  stepper,
  homeAlignedContent = false,
}) => {
  const styles = useHeroBannerStyles();
  const [imageFailed, setImageFailed] = useState(false);
  const showFigmaImage = !!backgroundImage && !imageFailed;

  if (figmaVariant) {
    return (
      <div className={styles.rootFigma} data-testid="hero-banner-figma-root">
        {showFigmaImage && (
          <>
            <div
              aria-hidden="true"
              data-testid="hero-banner-image-layer"
              className={styles.figmaImageLayer}
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
            <div aria-hidden="true" data-testid="hero-banner-scrim" className={styles.figmaScrim} />
            <img
              src={backgroundImage}
              alt=""
              aria-hidden="true"
              className={styles.figmaImageProbe}
              onError={() => setImageFailed(true)}
            />
          </>
        )}
        <div
          className={mergeClasses(
            styles.figmaInner,
            homeAlignedContent && styles.figmaInnerHomeAligned
          )}
          data-testid="hero-banner-figma-inner"
        >
          <div className={styles.figmaTopRow}>
            <div className={styles.figmaContent}>
              {backLink ? <div className={styles.figmaBackLink}>{backLink}</div> : null}
              {eyebrow ? (
                <Text className={styles.eyebrow} aria-hidden={false}>
                  {eyebrow}
                </Text>
              ) : null}
              <Text as="h1" className={styles.titleFigma}>
                {title}
              </Text>
              <Text className={styles.subtitleFigma}>{subtitle}</Text>
              {actions ? <div className={styles.figmaActions}>{actions}</div> : null}
            </div>
            {tipCard ? <div className={styles.figmaTipSlot}>{tipCard}</div> : null}
          </div>
          {stepper ? <div className={styles.figmaStepperSlot}>{stepper}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={mergeClasses(styles.root)}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.overlay}>
        <div className={styles.content}>
          <Text as="h1" className={styles.title}>
            {title}
          </Text>
          <Text className={styles.subtitle}>{subtitle}</Text>
        </div>
      </div>
    </div>
  );
};

HeroBanner.displayName = 'HeroBanner';
